import "@/App.css";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

// Auth + role utilities
import RequireAuth from "@/components/auth/RequireAuth";
import RequireRole from "@/components/auth/RequireRole";
import { dashboardFor } from "@/lib/roleRoute";

// Shells
import Layout from "@/components/Layout";
import PlatformLayout from "@/components/PlatformLayout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import UpdateNotifier from "@/components/UpdateNotifier";

// Auth + landing dashboards
import Login from "@/pages/Login";
const Dashboard = lazyRetry(() => import(/* webpackPrefetch: true */ "@/pages/Dashboard"));
const DoctorDashboard = lazyRetry(() => import(/* webpackPrefetch: true */ "@/pages/DoctorDashboard"));
const StaffDashboard = lazyRetry(() => import("@/pages/StaffDashboard"));

// Platform
const PlatformDashboard = lazyRetry(() => import("@/pages/PlatformDashboard"));
const PlatformHospitals = lazyRetry(() => import("@/pages/PlatformHospitals"));
const PlatformAdmins = lazyRetry(() => import("@/pages/PlatformAdmins"));
const PlatformDoctors = lazyRetry(() => import("@/pages/PlatformDoctors"));
const PlatformUsers = lazyRetry(() => import("@/pages/PlatformUsers"));

// Hospital pages
const Patients = lazyRetry(() => import(/* webpackPrefetch: true */ "@/pages/Patients"));
const PatientDetail = lazyRetry(() => import(/* webpackPrefetch: true */ "@/pages/PatientDetail"));
const Medicines = lazyRetry(() => import("@/pages/Medicines"));
const NewPrescription = lazyRetry(() => import("@/pages/NewPrescription"));
const PrescriptionView = lazyRetry(() => import("@/pages/PrescriptionView"));
const PrescriptionHistory = lazyRetry(() => import("@/pages/PrescriptionHistory"));
const PrintSettings = lazyRetry(() => import("@/pages/PrintSettings"));
const BulkUpload = lazyRetry(() => import("@/pages/BulkUpload"));
const Inventory = lazyRetry(() => import("@/pages/Inventory"));
const ScheduleSummary = lazyRetry(() => import("@/pages/ScheduleSummary"));
const VisitCalendar = lazyRetry(() => import("@/pages/VisitCalendar"));
const Expenses = lazyRetry(() => import("@/pages/Expenses"));
const Followups = lazyRetry(() => import("@/pages/Followups"));
const Referrals = lazyRetry(() => import("@/pages/Referrals"));
const ReferralView = lazyRetry(() => import("@/pages/ReferralView"));
const ReferralPartners = lazyRetry(() => import("@/pages/ReferralPartners"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const Users = lazyRetry(() => import("@/pages/Users"));
const Reception = lazyRetry(() => import(/* webpackPrefetch: true */ "@/pages/Reception"));
const Reservations = lazyRetry(() => import("@/pages/Reservations"));
const DoctorCalendar = lazyRetry(() => import("@/pages/DoctorCalendar"));
const PatientQR = lazyRetry(() => import("@/pages/PatientQR"));
const PatientCard = lazyRetry(() => import("@/pages/PatientCard"));
const KioskRegister = lazyRetry(() => import("@/pages/KioskRegister"));
const KioskCodes = lazyRetry(() => import("@/pages/KioskCodes"));
const PatientAccess = lazyRetry(() => import("@/pages/PatientAccess"));
const DoctorGroups = lazyRetry(() => import("@/pages/DoctorGroups"));
const DoctorBusiness = lazyRetry(() => import("@/pages/DoctorBusiness"));
const POS = lazyRetry(() => import("@/pages/POS"));
const POSTransaction = lazyRetry(() => import("@/pages/POSTransaction"));
const POSServices = lazyRetry(() => import("@/pages/POSServices"));
const POSReports = lazyRetry(() => import("@/pages/POSReports"));
const QueueDisplay = lazyRetry(() => import("@/pages/QueueDisplay"));
const UserManual = lazyRetry(() => import("@/pages/UserManual"));
const PublicBooking = lazyRetry(() => import("@/pages/PublicBooking"));
const BookingHome = lazyRetry(() => import("@/pages/booking/BookingHome"));
const BookingWizard = lazyRetry(() => import("@/pages/booking/BookingWizard"));
const BookingPayment = lazyRetry(() => import("@/pages/booking/BookingPayment"));
const BookingConfirmed = lazyRetry(() => import("@/pages/booking/BookingConfirmed"));
const HospitalPaymentQrSettings = lazyRetry(() => import("@/pages/HospitalPaymentQrSettings"));
const POSReceiptSettings = lazyRetry(() => import("@/pages/POSReceiptSettings"));
const DoctorScheduleSettings = lazyRetry(() => import("@/pages/DoctorScheduleSettings"));
const BrandingSettings = lazyRetry(() => import("@/pages/BrandingSettings"));

// Doctor mobile / PWA pages
const DoctorMobile = lazyRetry(() => import("@/pages/DoctorMobile"));
const DoctorMobilePatient = lazyRetry(() => import("@/pages/DoctorMobilePatient"));
const DoctorMobilePatients = lazyRetry(() => import("@/pages/DoctorMobilePatients"));
const ReceptionMobile = lazyRetry(() => import("@/pages/ReceptionMobile"));

// Dental
const DentalDashboard = lazyRetry(() => import("@/pages/dental/DentalDashboard"));
const DentalPatientChart = lazyRetry(() => import("@/pages/dental/DentalPatientChart"));
const DentalRecalls = lazyRetry(() => import("@/pages/dental/DentalRecalls"));

import ErrorBoundary from "@/components/ErrorBoundary";
import RouteFallback from "@/components/RouteFallback";
import { lazyRetry } from "@/lib/lazyRetry";

const HOSPITAL_ROLES = ["admin", "hospital_admin", "doctor", "staff", "reception", "cashier"];

function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent || "") || window.innerWidth < 768;
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

// "/" → role-aware redirect. For doctors on mobile/PWA, send them to /doctor-mobile.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const role = user.role;
  const clinicType = user.clinic_type || "medical";

  // A dental tenant has its own space, and it is tablet-first already — so it
  // wins over the phone-specific medical views rather than competing with them.
  if (clinicType === "dental") {
    const dentalDest = dashboardFor({ role, clinicType });
    return <Navigate to={dentalDest || "/login"} replace />;
  }

  const onMobile = isStandalonePWA() || isMobileDevice();
  if (role === "doctor" && onMobile) {
    return <Navigate to="/doctor-mobile" replace />;
  }
  if (role === "reception" && onMobile) {
    return <Navigate to="/reception-mobile" replace />;
  }

  const dest = dashboardFor({ role, clinicType });
  return <Navigate to={dest || "/login"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <UpdateNotifier />
        <PWAInstallPrompt />
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/book" element={<BookingHome />} />
          <Route path="/book/:slug" element={<BookingWizard />} />
          <Route path="/book/:slug/payment/:rid" element={<BookingPayment />} />
          <Route path="/book/:slug/confirmed/:rid" element={<BookingConfirmed />} />
          <Route path="/book-legacy/:slug" element={<PublicBooking />} />
          <Route path="/patient-card/:token" element={<PatientCard />} />
          <Route path="/kiosk/:slug" element={<KioskRegister />} />
          <Route path="/kiosk/:slug/:doctorId" element={<KioskRegister />} />

          {/* Authenticated tree */}
          <Route element={<RequireAuth />}>
            {/* Platform admin only */}
            <Route element={<RequireRole allow={["platform_admin"]} />}>
              <Route element={<PlatformLayout />}>
                <Route path="/platform" element={<PlatformDashboard />} />
                <Route path="/platform/dashboard" element={<PlatformDashboard />} />
                <Route path="/platform/hospitals" element={<PlatformHospitals />} />
                <Route path="/platform/admins" element={<PlatformAdmins />} />
                <Route path="/platform/doctors" element={<PlatformDoctors />} />
                <Route path="/platform/users" element={<PlatformUsers />} />
                <Route path="/platform/manual" element={<UserManual />} />
              </Route>
            </Route>

            {/* Hospital users inside the hospital Layout */}
            <Route element={<Layout />}>
              {/* Strict role dashboards */}
              <Route element={<RequireRole allow={["admin", "hospital_admin"]} />}>
                <Route path="/admin/dashboard" element={<Dashboard />} />
              </Route>
              <Route element={<RequireRole allow={["doctor"]} />}>
                <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
              </Route>
              <Route element={<RequireRole allow={["staff", "reception"]} />}>
                <Route path="/staff/dashboard" element={<StaffDashboard />} />
              </Route>

              {/* Shared hospital pages */}
              <Route element={<RequireRole allow={HOSPITAL_ROLES} />}>
                <Route path="/patients" element={<Patients />} />
                <Route path="/patients/bulk-upload" element={<BulkUpload kind="patients" />} />
                <Route path="/patients/:id" element={<PatientDetail />} />
                <Route path="/medicines" element={<Medicines />} />
                <Route path="/medicines/bulk-upload" element={<BulkUpload kind="medicines" />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/followups" element={<Followups />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/referrals/:id/preview" element={<ReferralView />} />
                <Route path="/referral-partners" element={<ReferralPartners />} />
                <Route path="/prescriptions" element={<PrescriptionHistory />} />
                <Route path="/prescriptions/:id/preview" element={<PrescriptionView />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/manual" element={<UserManual />} />
                <Route path="/queue-screen" element={<QueueDisplay />} />
                <Route path="/queue-display" element={<QueueDisplay />} />
              </Route>

              {/* Reception / clinic-front desk */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "reception"]} />}>
                <Route path="/reception" element={<Reception />} />
                <Route path="/kiosk-codes" element={<KioskCodes />} />
              </Route>

              {/* Reservations + calendars (admin/doctor/reception) */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception"]} />}>
                <Route path="/reservations" element={<Reservations />} />
                <Route path="/doctor-calendar" element={<DoctorCalendar />} />
                <Route path="/schedule-summary" element={<ScheduleSummary />} />
                <Route path="/patient-calendar" element={<VisitCalendar />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/patient-qr" element={<PatientQR />} />
              </Route>

              {/* POS / Billing (admin/doctor/reception/cashier) */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception", "cashier"]} />}>
                <Route path="/pos" element={<POS />} />
                <Route path="/pos/transactions/:id" element={<POSTransaction />} />
                <Route path="/pos/services" element={<POSServices />} />
                <Route path="/pos/reports" element={<POSReports />} />
              </Route>

              {/* Admin-only */}
              <Route element={<RequireRole allow={["admin", "hospital_admin"]} />}>
                <Route path="/patient-access" element={<PatientAccess />} />
                <Route path="/doctor-groups" element={<DoctorGroups />} />
                <Route path="/users" element={<Users />} />
                <Route path="/settings/branding" element={<BrandingSettings />} />
                <Route path="/settings/receipt" element={<POSReceiptSettings />} />
              </Route>

              {/* Doctor-capable (legacy doctor-dashboard, business, print settings) */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
                <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
                <Route path="/doctor-business" element={<DoctorBusiness />} />
                <Route path="/prescriptions/new" element={<NewPrescription />} />
                <Route path="/prescriptions/:id/edit" element={<NewPrescription />} />
                <Route path="/settings" element={<PrintSettings />} />
                <Route path="/settings/payment-qr" element={<HospitalPaymentQrSettings />} />
              </Route>

              {/* Dental space — a dental clinic is its own hospital tenant */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
                <Route path="/dental" element={<DentalDashboard />} />
                <Route path="/dental/patients/:id/chart" element={<DentalPatientChart />} />
              </Route>
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception", "staff"]} />}>
                <Route path="/dental/recalls" element={<DentalRecalls />} />
              </Route>

              {/* Doctor schedule — doctors edit their own; admins & reception manage any */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception"]} />}>
                <Route path="/doctor/schedule" element={<DoctorScheduleSettings />} />
              </Route>
            </Route>

            {/* Doctor mobile / PWA — FULL SCREEN, no sidebar Layout */}
            <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
              <Route path="/doctor-mobile" element={<DoctorMobile />} />
              <Route path="/doctor-mobile/patient/:id" element={<DoctorMobilePatient />} />
              <Route path="/doctor-mobile/patients" element={<DoctorMobilePatients />} />
            </Route>

            {/* Reception mobile / PWA — FULL SCREEN, no sidebar Layout */}
            <Route element={<RequireRole allow={["admin", "hospital_admin", "reception"]} />}>
              <Route path="/reception-mobile" element={<ReceptionMobile />} />
            </Route>
          </Route>

          {/* Root + catch-all → role-aware redirect */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
